// Pure factual checks shared by the server and the unsaved Product Writer preview.
// No provider, credentials, prompts or automatic rewriting belong here.
const FAILURE = 'qualification_preservation_failed';
const normalise = text => text.toLowerCase().replace(/[\u2010-\u2015]/g, '-').replace(/\s+/g, ' ').trim();
const clauses = text => text.split(/[.!?;\n]+/).flatMap(part => normalise(part).split(/,?\s+(?:but|while)\s+|\s+and\s+(?=(?:the )?(?:lighting|power|branding|an? operator|an? attendant)\b)/)).filter(Boolean);
const capabilities = [
  /\b(?:small groups?|groups?|couples|together)\b/,
  /\b(?:individuals?|solo|one person|one guest)\b/,
  /\b(?:brand\w*|overlays?|logos?)\b/,
  /\b(?:lighting|lights?)\b/,
  /\b(?:operators?|attendants?|staff|supervis\w*)\b/,
  /\b(?:power|electric\w*)\b/,
  /\b(?:sharing|shared|share|downloads?|email|sms)\b/,
  /\b(?:print\w*)\b/,
  /\b(?:private celebrations?|private parties)\b/,
];
// Deliberately bounded equivalents. Unknown paraphrases fail closed for Admin review.
const conditions = [
  /\b(?:depending on|dependent on|subject to) (?:the )?(?:event )?set[ -]?up\b/,
  /\b(?:supplied|provided) (?:and|&) approved\b|\bapproved (?:and|&) (?:supplied|provided)\b/,
  /\b(?:as required|when required|where required|as needed|when needed|where needed)\b/,
  /\b(?:for|at|during) (?:normal )?managed events\b/,
  /\b(?:where appropriate|when appropriate|where suitable|when suitable)\b/,
  /\b(?:optional|as an option)\b/,
  /\bmay (?:be required|be needed)\b/,
];
const weakened = /\b(?:not|never|no longer|regardless|irrespective|without|whether or not|always|guaranteed|every event|all events)\b/;
const stopWords = new Set(('a an and are as at be can does event events for from has have in is it its of on or our product provided supplied that the their them these they this to uses using was were what when where which with you your available included required may must only depending setup').split(' '));
function words(text) { return [...new Set((text.match(/[a-z]{3,}/g) || []).filter(w => !stopWords.has(w)))]; }
function requirements(context) {
  const source = [context.description, context.proposal, context.features, context.equipment,
    context.operationsVerified ? context.operations : '', context.eventTypes, context.specs,
    context.mediaCaptions, context.clarifications].filter(s => typeof s === 'string').join('\n');
  const rules = [];
  for (const claim of clauses(source)) {
    // Instructions/unknown capabilities are not positive reviewed facts.
    if (/^(?:do not|never|don't)\b/.test(claim)) continue;
    const matches = conditions.filter(condition => condition.test(claim));
    // Other explicit conditional clauses are retained literally, not guessed equivalent.
    const other = claim.match(/\b(?:if|unless|provided that|subject to|depending on|where|when)\b[^,]+$/);
    if (other && !matches.some(condition => condition.test(other[0]))) {
      const exact = other[0].trim();
      matches.push({test: value => value.includes(exact)});
    }
    if (!matches.length) continue;
    const known = capabilities.filter(capability => capability.test(claim));
    // For an unlisted capability, bind the condition to concrete source words.
    // Conservative lexical fallback: it can block an unfamiliar paraphrase, never infer facts.
    const subject = words(other ? claim.slice(0, other.index) : claim.replace(/\b(?:as required|as needed|for managed events|where appropriate)\b/g, ''));
    const claims = known.length ? known : subject.map(word => new RegExp('\\b' + word + '\\b'));
    for (const capability of claims) for (const condition of matches) rules.push({capability, condition});
  }
  return rules;
}
function qualificationWarnings(context, text) {
  const rules = requirements(context);
  return clauses(text).some(claim => rules.some(({capability, condition}) =>
    capability.test(claim) && (!condition.test(claim) || weakened.test(claim)))) ? [FAILURE] : [];
}
function productFieldWarnings(context, draft) {
  const retainedFeatures = new Set(typeof context.features === 'string' ? context.features.split('\n').map(s => s.trim()).filter(Boolean) : []);
  return {
    short_description: qualificationWarnings(context, draft.short_description),
    proposal_description: qualificationWarnings(context, draft.proposal_description),
    // Verbatim reviewed features have not lost any wording. Check any new/changed
    // feature independently; one qualified feature cannot cover another.
    key_features: draft.key_features.some(feature => !retainedFeatures.has(feature) && qualificationWarnings(context, feature).length) ? [FAILURE] : [],
  };
}
module.exports = {qualificationWarnings, productFieldWarnings};
